import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Drawer, styled, useTheme } from '@mui/material/'
import useLogout from '../hooks/useLogout'
import useHttpApiNew from '../hooks/useHttpApiNew'
import { useActionLogs } from '../store/actionLogsContext'
import { NewApiBaseUrl } from '../consts/new-api-base-url'
import { DESKTOP_HISTORY_DRAWER_WIDTH_PX } from '../consts/form-drawer-width'
import { canUserAccessActionHistory } from './Statistics/statistics-report-type'
import { NotificationCenter } from '../components/notifications/notification-center'
import { getNotificationTarget } from '../components/notifications/notification-target.util'
import { getNotificationViewAvailability } from '../components/notifications/notification-view-availability.util'
import { NOTIFICATION_TARGETS, NOTIFICATION_VIEW_AVAILABILITY } from '../components/notifications/notification-center.const'

const getUsersListUrl = new URL(`${NewApiBaseUrl}/Auth/GetList`)

const TARGET_ROUTES = {
  [NOTIFICATION_TARGETS.USERS]: { path: '/admin', state: { adminSection: 'users' } },
  [NOTIFICATION_TARGETS.CSV_IMPORTS]: { path: '/admin', state: { adminSection: 'csvImport' } },
  [NOTIFICATION_TARGETS.ZIP_IMPORTS]: { path: '/package-imports', state: {} },
}

const StyledDrawer = styled(Drawer)(() => ({
  width: DESKTOP_HISTORY_DRAWER_WIDTH_PX,
  '& .MuiDrawer-paper': { width: DESKTOP_HISTORY_DRAWER_WIDTH_PX, height: '100vh' },
}))

function readStoredSession() {
  try {
    const raw = localStorage.getItem('adams_user')
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function useHistoryUsers(token) {
  const { request } = useHttpApiNew()
  const [users, setUsers] = useState([])

  useEffect(() => {
    if (!token) return
    request(getUsersListUrl, 'GET', null, { Authorization: `Bearer ${token}` }).then(response => {
      if (response && !response.responseAnyError) setUsers(response.responseData ?? [])
    })
  }, [token, request])

  return users
}

/**
 * Notification Center host: persistent side drawer on desktop, full-screen panel on mobile.
 * Selecting a crash notification previews it on the map (`onShowCrashOnMap`); "View" opens its form (`onOpenCrash`).
 */
export default function History({ open, isMobile, onShowCrashOnMap, onOpenCrash, onClose }) {
  const navigate = useNavigate()
  const location = useLocation()
  const logout = useLogout()
  const theme = useTheme()
  const { setAsRead, refreshKey } = useActionLogs()
  const state = useMemo(() => location.state || {}, [location.state])
  const storedSession = useMemo(readStoredSession, [])
  const token = state.token || storedSession?.token || localStorage.getItem('token') || null
  const sessionUser = state.init?.user ?? storedSession?.init?.user ?? null
  const users = useHistoryUsers(token)

  useEffect(() => {
    if (!token) logout()
  }, [token, logout])

  useEffect(() => {
    if (!canUserAccessActionHistory(sessionUser)) {
      navigate('/home', { state: { ...state, isHistoryOpen: false }, replace: true })
    }
  }, [sessionUser, navigate, state])

  const getViewAvailability = useCallback(
    notification => getNotificationViewAvailability(notification, { userType: sessionUser?.type, isMobile }),
    [sessionUser, isMobile],
  )

  const closeOnMobile = useCallback(() => {
    if (isMobile) onClose()
  }, [isMobile, onClose])

  const handleSelect = useCallback(notification => {
    if (getNotificationTarget(notification) !== NOTIFICATION_TARGETS.CRASH || !onShowCrashOnMap) return
    onShowCrashOnMap(notification)
    closeOnMobile()
  }, [onShowCrashOnMap, closeOnMobile])

  const handleView = useCallback(notification => {
    if (getViewAvailability(notification) !== NOTIFICATION_VIEW_AVAILABILITY.ENABLED) return
    const target = getNotificationTarget(notification)
    if (target === NOTIFICATION_TARGETS.CRASH) {
      onOpenCrash?.(notification)
      closeOnMobile()
      return
    }
    const route = TARGET_ROUTES[target]
    navigate(route.path, { state: { ...state, ...route.state, isHistoryOpen: false } })
  }, [getViewAvailability, onOpenCrash, closeOnMobile, navigate, state])

  const center = (
    <NotificationCenter
      token={token}
      users={users}
      refreshKey={refreshKey}
      getViewAvailability={getViewAvailability}
      onClose={onClose}
      onMarkRead={setAsRead}
      onSelect={handleSelect}
      onView={handleView}
    />
  )

  if (isMobile) {
    return (
      <Drawer anchor="bottom" open={open} onClose={onClose} sx={{ zIndex: theme.zIndex.drawer + 2 }} PaperProps={{ sx: { height: '100%' } }}>
        {center}
      </Drawer>
    )
  }

  return (
    <StyledDrawer variant="persistent" anchor={theme.direction === 'rtl' ? 'left' : 'right'} open={open}>
      {center}
    </StyledDrawer>
  )
}
